import { useEffect, useMemo, useState } from "react";
import { ClipboardList, Search, RefreshCw, Download, CheckCircle2, Users, PieChart } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { fmtDateTime } from "@/lib/format";

/**
 * AdminSurvey — /admin/survey
 *
 * Combines two engagement signals for the external QuestionPro survey:
 *   (A) OPENED    — auto-logged when a member opens /survey inside the app.
 *   (B) COMPLETED — self-reported by the member via the "I finished the
 *                   survey" button on /survey.
 *
 * Shows a summary card + a searchable list of members with counts and
 * timestamps, plus a CSV export.
 */
export default function AdminSurvey() {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ summary: {}, items: [] });
  const [q, setQ] = useState("");
  const [exporting, setExporting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/admin/survey/activity");
      setData(resp);
    } catch (err) {
      console.error("AdminSurvey: failed to load activity", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const items = data?.items || [];
    if (!q.trim()) return items;
    const lq = q.trim().toLowerCase();
    return items.filter((it) =>
      (it.full_name || "").toLowerCase().includes(lq)
      || (it.phone || "").includes(q.trim())
    );
  }, [data, q]);

  const summary = data?.summary || {};
  const totalMembers = summary.total_members || 0;
  const openedMembers = summary.opened_members || 0;
  const completedMembers = summary.completed_members || 0;
  const completionPct = (summary.completion_rate || 0) * 100;

  const onExportCSV = async () => {
    setExporting(true);
    try {
      // We reuse the authenticated axios instance to fetch the raw CSV so
      // the Authorization header is included, then trigger a download.
      const resp = await api.get("/admin/survey/activity/export", {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `survey-activity-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(lang === "es" ? "Descarga iniciada" : "Download started");
    } catch (err) {
      // As a fallback (some setups block blob downloads via axios), try the
      // simplest thing: open the API URL — will only work if server accepts
      // cookies/session, which we do not use, so we surface an error only.
      console.error("AdminSurvey: export failed", err, API_BASE);
      toast.error(lang === "es"
        ? "No se pudo generar el CSV. Intenta de nuevo."
        : "Could not generate the CSV. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-survey-page">
      <div>
        <h1 className="font-serif-display text-2xl font-semibold flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-primary" />
          {lang === "es" ? "Actividad de la encuesta" : "Survey activity"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {lang === "es"
            ? "Combinamos dos senales: quien ha ABIERTO la encuesta desde la app y quien ha CONFIRMADO haberla terminado con el boton dentro de la app."
            : "We combine two signals: who has OPENED the survey inside the app, and who has CONFIRMED completion by tapping the button inside the app."}
        </p>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Users className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {lang === "es" ? "Miembros registrados" : "Registered members"}
              </p>
              <p className="font-serif-display text-xl font-semibold" data-testid="survey-summary-total">
                {loading ? "..." : totalMembers}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <ClipboardList className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {lang === "es" ? "Han abierto" : "Opened"}
              </p>
              <p className="font-serif-display text-xl font-semibold" data-testid="survey-summary-opened">
                {loading ? "..." : openedMembers}
              </p>
              {totalMembers > 0 && !loading && (
                <p className="text-[11px] text-muted-foreground">
                  {Math.round((openedMembers / totalMembers) * 100)}% {lang === "es" ? "del total" : "of total"}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl ring-1 ring-emerald-100 shadow-sm bg-emerald-50/40">
          <CardContent className="p-5 flex items-center gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-wider text-emerald-800/70">
                {lang === "es" ? "Confirmaron completada" : "Confirmed completed"}
              </p>
              <p className="font-serif-display text-xl font-semibold text-emerald-900" data-testid="survey-summary-completed">
                {loading ? "..." : completedMembers}
              </p>
              {!loading && (
                <p className="text-[11px] text-emerald-800/80 inline-flex items-center gap-1">
                  <PieChart className="h-3 w-3" />
                  {completionPct.toFixed(1)}% {lang === "es" ? "de compleción" : "completion"}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[12rem]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={lang === "es" ? "Buscar por nombre o telefono..." : "Search by name or phone..."}
              className="pl-9 h-10"
              data-testid="survey-search"
            />
          </div>
          <Button variant="ghost" size="sm" onClick={reload} disabled={loading} data-testid="survey-reload">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            <span className="ml-1 hidden sm:inline">{lang === "es" ? "Recargar" : "Refresh"}</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onExportCSV}
            disabled={exporting || !filtered.length}
            data-testid="survey-export"
          >
            <Download className="h-4 w-4" />
            <span className="ml-1 hidden sm:inline">CSV</span>
          </Button>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm overflow-hidden" data-testid="survey-list-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={`row-${i}`} className="h-12 w-full rounded-xl" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="survey-list-empty">
              {q
                ? (lang === "es" ? "Nadie coincide con la busqueda." : "No one matches the search.")
                : (lang === "es"
                    ? "Aun no hay actividad. Cuando alguien abra la encuesta desde la app aparecera aqui."
                    : "No activity yet. As soon as someone opens the survey from the app it will show up here.")}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{lang === "es" ? "Miembro" : "Member"}</TableHead>
                    <TableHead className="whitespace-nowrap">{lang === "es" ? "Telefono" : "Phone"}</TableHead>
                    <TableHead className="whitespace-nowrap">{lang === "es" ? "Aperturas" : "Opens"}</TableHead>
                    <TableHead className="whitespace-nowrap">{lang === "es" ? "Primera vez" : "First opened"}</TableHead>
                    <TableHead className="whitespace-nowrap">{lang === "es" ? "Ultima vez" : "Last opened"}</TableHead>
                    <TableHead className="whitespace-nowrap">{lang === "es" ? "Completada" : "Completed"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((it) => (
                    <TableRow key={it.member_id || `anon-${it.first_opened_at || Math.random()}`} data-testid={`survey-row-${it.member_id || "anon"}`}>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-semibold">
                            {(it.full_name || "?").trim().charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">
                              {it.full_name || (lang === "es" ? "(Sin nombre)" : "(No name)")}
                            </p>
                            {it.language && (
                              <p className="text-[11px] text-muted-foreground">
                                {(it.language || "").toUpperCase()}
                              </p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {it.phone || "—"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="rounded-full">
                          {it.opened_count || 0}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {it.first_opened_at ? fmtDateTime(it.first_opened_at, lang) : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {it.last_opened_at ? fmtDateTime(it.last_opened_at, lang) : "—"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {it.completed_at ? (
                          <span
                            className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200"
                            data-testid={`survey-row-completed-${it.member_id || "anon"}`}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            {fmtDateTime(it.completed_at, lang)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground">
        {lang === "es"
          ? "Nota: 'Han abierto' se registra automaticamente cuando un miembro entra a la pantalla de la encuesta desde la app. 'Confirmaron completada' es un boton que el miembro toca despues de terminar la encuesta externa (QuestionPro)."
          : "Note: 'Opened' is auto-recorded when a member enters the in-app survey screen. 'Confirmed completed' is a button members tap after finishing the external (QuestionPro) survey."}
      </p>
    </div>
  );
}
